/*
  Plate designer (task 41, products/engraved-neck-plate/brief.md). Engraved neck plates: the customer's text (and, on
  the image product, their image) drawn as an engraving on a real photo of the plate, straight on, and on a guitar back.

  Everything is worked out in plate millimetres: origin at the plate centre, x right, y down, the top of the plate
  being the edge towards the neck. The engraving is drawn once on a "face" canvas (FACE_K px per mm) and then laid
  on each view. Text block and image each have a pose, like the custom skin (tgf-custom-skin.js):
    dx, dy  the centre in mm from the plate centre
    s       size, 1 = as in the layout (text) or 36 mm wide at most (image)
    a       degrees, clockwise
  Saved as _text_pose / _image_pose "dx=..;dy=..;s=..;a=..", plus a readable "Text position" / "Image position".
*/
(() => {
  if (customElements.get('tgf-plate-designer')) return;

  const FACE_K = 14; // face canvas, px per mm
  const VIEW = 1000; // editor and guitar canvases are 1000 x 1000
  const SMALL_FONT = 'Source Sans Pro Bold';
  const FINISHES = ['chrome', 'black', 'gold'];
  const HOLE_R = 4.2; // screw head, mm: nothing is engraved under it
  const KEEP_OUT = { edge: 2.5, hole: 6 }; // the dashed engraving zone: mm from the edge, radius around each hole
  // What the laser engraves cleanly (to confirm with a test plate): smallest letter size (font size, mm) and line width
  const MIN_FONT_MM = 2.6;
  const MIN_FONT_THIN_MM = 6; // hairline scripts
  const THIN_FONTS = ['Claster', 'Autograph', 'One Love'];
  const MIN_LINE_MM = 0.3;
  const IMAGE_BOX = [36, 40]; // an image first fits in this box, mm

  // [dark, light] of the engraving on each plate: the laser removes the finish and leaves a pale matte line, colours
  // sampled from real plates (Berna's photo 2026-11-03). Third value: strength of a small shadow, none = flush.
  const INK = {
    chrome: ['#c9cbcd', '#e8e9eb', 0],
    black: ['#d0d0d4', '#f0f0f4', 0],
    gold: ['#efcbb6', '#fde2cf', 0],
  };

  // Distance from a line's centre down to its baseline, in em. canvas textBaseline 'middle' lands up to 0.2 em apart
  // between browsers, 3 mm on a plate: this fixes it, and the engraver's file uses the same table
  // (task 26 pipeline/plate/engrave_plate.py). Values: Chrome's own 'middle' for each font, measured 2026-11-03.
  const BASELINE = {
    'Claster': 0.220, 'Dancing Script': 0.267, 'Great Vibes': 0.180, 'Pacifico': 0.242, 'Harlow': 0.295,
    'Autograph': 0.204, 'Berosong': 0.233, 'Bloom & Blush': 0.250, 'Buka Bird': 0.249, 'Circus Age': 0.273,
    'Edo': 0.276, 'Gravity': 0.250, 'Hellshunx': 0.405, 'Luckiest Guy': 0.203, 'Maytorm': 0.205,
    'One Love': 0.300, 'Vtks Revolt': 0.199, 'Bebas Neue': 0.250, 'Source Sans Pro Bold': 0.250,
  };

  // Text layouts, block coordinates in mm (block centre = 0, 0). size = font size in mm, maxW = widest a line gets.
  const LAYOUTS = {
    name: { lines: [{ f: 'main', size: 14, y: 0, maxW: 40 }] },
    classic: {
      lines: [
        { f: 'top', size: 3.8, y: -12.5, maxW: 34, small: true },
        { f: 'main', size: 10, y: 0, maxW: 40 },
        { f: 'bottom', size: 4.2, y: 12, maxW: 36, small: true },
      ],
    },
    serial: {
      font: 'Bebas Neue',
      ph: { top: 'NO.', main: '0427', bottom: 'James K.' },
      lines: [
        { f: 'top', size: 4, y: -13, maxW: 30, small: true },
        { f: 'main', size: 17, y: -0.5, maxW: 36 },
        { f: 'bottom', size: 4.6, y: 12.5, maxW: 34, small: true },
      ],
    },
    frame: {
      frame: [31, 44],
      lines: [
        { f: 'main', size: 8, y: -2, maxW: 25 },
        { f: 'bottom', size: 3.8, y: 7.5, maxW: 24, small: true },
      ],
    },
  };

  const fam = (name) => 'tgf-pd-' + name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const roundRect = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  };

  class PlateDesigner extends HTMLElement {
    connectedCallback() {
      const json = (sel) => JSON.parse(this.querySelector(sel).textContent);
      this.variants = json('[data-pd-variants]');
      this.assets = json('[data-pd-assets]');
      this.geo = this.assets.geometry;
      this.imageMode = this.dataset.mode === 'image';
      this.form = document.getElementById(this.dataset.form);
      this.canvas = this.querySelector('[data-pd-canvas]');
      this.ctx = this.canvas.getContext('2d');
      this.view = 'plate';
      this.finish = 'chrome';
      [this.PW, this.PH] = this.geo.plate.map((v) => v / 2);

      this.face = this.makeCanvas(this.geo.plate[0] * FACE_K, this.geo.plate[1] * FACE_K, true);
      this.ghost = this.makeCanvas(this.face.width, this.face.height);
      this.items = { text: this.defaultPose('text') };
      if (this.imageMode) this.items.image = this.defaultPose('image');
      this.selected = this.imageMode ? 'image' : 'text';
      this.bounds = {};

      const q = (sel) => this.querySelector(sel);
      this.el = {
        size: q('[data-pd-size]'),
        turn: q('[data-pd-turn]'),
        guides: q('[data-pd-guides]'),
        safe: q('[data-pd-safe]'),
        fine: q('[data-pd-fine]'),
        missing: q('[data-pd-missing]'),
        proof: q('[data-pd-proof]'),
        proofPhoto: q('[data-pd-proof-photo]'),
        notes: q('[data-pd-notes]'),
        notesInput: q('[data-pd-notes-input]'),
        file: q('[data-pd-file]'),
        fileName: q('[data-pd-file-name]'),
        fileWarning: q('[data-pd-file-warning]'),
        imageTools: q('[data-pd-image-tools]'),
        threshold: q('[data-pd-threshold]'),
        invert: q('[data-pd-invert]'),
        rights: q('[data-pd-rights]'),
        textPosition: q('[data-pd-text-position]'),
        textPose: q('[data-pd-text-pose]'),
        imagePosition: q('[data-pd-image-position]'),
        imagePose: q('[data-pd-image-pose]'),
      };
      this.inputs = {};
      this.querySelectorAll('[data-pd-text]').forEach((input) => {
        this.inputs[input.dataset.pdText] = input;
        input.dataset.placeholder = input.placeholder;
      });

      this.images = {};
      this.loadFonts();
      this.bindControls();
      this.setupPointer();
      this.guard();
      this.syncFromVariant();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(() => this.syncFromVariant(), 0));
      }
      this.layoutChanged(false);
    }

    makeCanvas(w, h, readBack) {
      const c = document.createElement('canvas');
      c.width = Math.round(w);
      c.height = Math.round(h);
      if (readBack) c.getContext('2d', { willReadFrequently: true });
      return c;
    }

    defaultPose(kind) {
      if (kind === 'image') return { dx: 0, dy: -5, s: 1, a: 0 };
      return this.imageMode ? { dx: 0, dy: 21, s: 0.6, a: 0 } : { dx: 0, dy: 0, s: 1, a: 0 };
    }

    /* ---------- assets ---------- */

    image(src) {
      if (!this.images[src]) {
        const img = new Image();
        img.onload = () => this.draw();
        img.src = src;
        this.images[src] = img;
      }
      const img = this.images[src];
      return img.complete && img.naturalWidth ? img : null;
    }

    loadFonts() {
      this.fontsReady = {};
      this.loadFont = (name) => {
        if (this.fontsReady[name]) return this.fontsReady[name];
        const face = new FontFace(fam(name), `url(${this.assets.fonts[name]})`);
        this.fontsReady[name] = face.load().then(
          (f) => {
            document.fonts.add(f);
            this.changed();
          },
          () => null
        );
        return this.fontsReady[name];
      };
      this.loadFont(SMALL_FONT);
      this.loadFont(this.fontName());
      this.querySelectorAll('[data-pd-font]').forEach((r) => {
        r.parentElement.querySelector('[data-pd-font-sample]').style.fontFamily = `'${fam(r.value)}', sans-serif`;
      });
      const all = () => this.assets.titleFonts.forEach(this.loadFont);
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
              all();
              io.disconnect();
            }
          },
          { rootMargin: '400px' }
        );
        io.observe(this);
      } else all();
    }

    fontName() {
      const r = this.querySelector('[data-pd-font]:checked');
      return r ? r.value : this.assets.titleFonts[0];
    }

    layout() {
      const r = this.querySelector('[data-pd-layout]:checked');
      return r ? r.dataset.pdLayout : 'classic';
    }

    /* ---------- controls ---------- */

    bindControls() {
      this.querySelectorAll('[data-pd-layout]').forEach((r) => r.addEventListener('change', () => this.layoutChanged(true)));
      this.querySelectorAll('[data-pd-text], [data-pd-font]').forEach((el) => el.addEventListener('input', () => this.changed()));
      this.querySelectorAll('[data-pd-font]').forEach((el) => el.addEventListener('change', () => this.changed()));
      this.querySelectorAll('[data-pd-view]').forEach((b) => b.addEventListener('click', () => this.setView(b.dataset.pdView)));
      this.querySelectorAll('[data-pd-target]').forEach((r) =>
        r.addEventListener('change', () => r.checked && this.select(r.value))
      );
      this.el.guides.addEventListener('change', () => this.draw());
      this.el.size.addEventListener('input', () => this.setPose({ s: Number(this.el.size.value) / 100 }));
      this.el.turn.addEventListener('input', () => this.setPose({ a: Number(this.el.turn.value) }));
      this.querySelector('[data-pd-turn90]').addEventListener('click', () => {
        const a = this.items[this.selected].a + 90;
        this.setPose({ a: a > 180 ? a - 360 : a });
      });
      this.querySelector('[data-pd-reset]').addEventListener('click', () => this.setPose(this.defaultPose(this.selected)));
      this.el.notes.addEventListener('input', () => {
        const v = this.el.notes.value.trim();
        this.el.notesInput.value = v;
        this.el.notesInput.disabled = !v;
      });
      this.el.proof.addEventListener('change', () => {
        if (this.el.proof.dataset.locked) this.el.proof.checked = true;
        this.writeSummaries();
      });
      this.querySelectorAll('[data-pd-kind]').forEach((r) => r.addEventListener('change', () => this.kindChanged()));
      if (this.el.file) {
        this.el.file.addEventListener('change', () => this.readFile());
        this.el.threshold.addEventListener('input', () => this.buildImageMask());
        this.el.invert.addEventListener('change', () => this.buildImageMask());
        this.el.rights.addEventListener('change', () => this.el.rights.checked && (this.el.missing.hidden = true));
      }
    }

    // Photos always get a proof: we turn them into line art first.
    kindChanged() {
      const photo = !!this.querySelector('[data-pd-kind][value="Photo"]:checked');
      if (photo) {
        this.el.proof.checked = true;
        this.el.proof.dataset.locked = '1';
      } else {
        delete this.el.proof.dataset.locked;
      }
      if (this.el.proofPhoto) this.el.proofPhoto.hidden = !photo;
      this.writeSummaries();
    }

    layoutChanged(byCustomer) {
      const name = this.layout();
      const l = LAYOUTS[name];
      const used = l.lines.map((line) => line.f);
      Object.entries(this.inputs).forEach(([key, input]) => {
        const on = used.includes(key);
        input.closest('[data-pd-line]').hidden = !on;
        input.disabled = !on;
        input.placeholder = (l.ph && l.ph[key]) || input.dataset.placeholder;
      });
      if (byCustomer && l.font) {
        const r = this.querySelector(`[data-pd-font][value="${l.font}"]`);
        if (r) r.checked = true;
      }
      this.changed();
    }

    setView(view) {
      this.view = view;
      this.querySelectorAll('[data-pd-view]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.pdView === view)));
      this.draw();
    }

    select(kind) {
      this.selected = kind;
      const r = this.querySelector(`[data-pd-target][value="${kind}"]`);
      if (r) r.checked = true;
      this.showPose();
      this.draw();
    }

    setPose(change) {
      const pose = Object.assign(this.items[this.selected], change);
      pose.s = clamp(Math.round(pose.s * 100) / 100, 0.4, 2);
      pose.a = Math.round(pose.a);
      pose.dx = Math.round(clamp(pose.dx, -this.PW, this.PW) * 10) / 10;
      pose.dy = Math.round(clamp(pose.dy, -this.PH, this.PH) * 10) / 10;
      this.activeUntil = Date.now() + 1200;
      clearTimeout(this.selectionTimer);
      this.selectionTimer = setTimeout(() => this.draw(), 1300);
      this.showPose();
      this.changed();
    }

    showPose() {
      const p = this.items[this.selected];
      this.el.size.value = Math.round(p.s * 100);
      this.el.turn.value = p.a;
    }

    syncFromVariant() {
      const idInput = this.form && this.form.querySelector('[name="id"]');
      const id = idInput ? Number(idInput.value) : 0;
      const v = this.variants.find((x) => x.id === id) || this.variants[0];
      if (!v) return;
      this.finish = FINISHES[v.plate] || 'chrome';
      this.changed();
    }

    /* ---------- the customer's image ---------- */

    async readFile() {
      const file = this.el.file.files && this.el.file.files[0];
      this.el.fileWarning.hidden = true;
      if (!file) return;
      const fail = () => {
        this.el.file.value = '';
        this.el.fileWarning.hidden = false;
      };
      if (file.size > 20 * 1024 * 1024) return fail();
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const k = Math.min(1, 900 / Math.max(img.naturalWidth, img.naturalHeight));
        const c = this.makeCanvas(img.naturalWidth * k, img.naturalHeight * k);
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0, c.width, c.height);
        const data = ctx.getImageData(0, 0, c.width, c.height).data;
        const lum = new Float32Array(c.width * c.height);
        const alpha = new Float32Array(c.width * c.height);
        for (let i = 0; i < lum.length; i += 1) {
          lum[i] = (0.3 * data[i * 4] + 0.59 * data[i * 4 + 1] + 0.11 * data[i * 4 + 2]) / 255;
          alpha[i] = data[i * 4 + 3] / 255;
        }
        this.src = { w: c.width, h: c.height, lum, alpha };
        this.imgMask = c;
        URL.revokeObjectURL(url);
        this.el.fileName.textContent = file.name;
        this.el.imageTools.hidden = false;
        this.el.missing.hidden = true;
        this.items.image = this.defaultPose('image');
        this.select('image');
        this.buildImageMask();
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        fail();
      };
      img.src = url;
    }

    // Dark parts of the image are engraved (or light ones, inverted); transparent parts never are.
    buildImageMask() {
      if (!this.src) return;
      const { w, h, lum, alpha } = this.src;
      const t = Number(this.el.threshold.value) / 100;
      const inv = this.el.invert.checked;
      const ctx = this.imgMask.getContext('2d');
      const out = ctx.createImageData(w, h);
      for (let i = 0; i < lum.length; i += 1) {
        const l = inv ? 1 - lum[i] : lum[i];
        out.data[i * 4 + 3] = clamp((t - l) / 0.08 + 0.5, 0, 1) * alpha[i] * 255;
      }
      ctx.putImageData(out, 0, 0);
      this.changed();
    }

    /* ---------- the engraving (face canvas, mm) ---------- */

    changed() {
      const main = this.inputs.main;
      const sample = (main && main.value.trim()) || (main && main.placeholder) || '';
      this.querySelectorAll('[data-pd-font-sample]').forEach((s) => (s.textContent = sample));
      this.querySelectorAll('[data-pd-line]').forEach((box) => {
        const input = box.querySelector('[data-pd-text]');
        box.querySelector('[data-pd-count]').textContent = input.value ? `${input.value.length} / ${input.maxLength}` : '';
      });
      this.loadFont(this.fontName());
      this.el.missing.hidden = true;
      this.renderFace();
      this.writeProperties();
      this.writeSummaries();
      this.draw();
      clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(() => this.attachPreview(), 600);
    }

    hasText() {
      return LAYOUTS[this.layout()].lines.some((l) => this.inputs[l.f].value.trim());
    }

    renderFace() {
      const real = this.face.getContext('2d');
      const ghost = this.ghost.getContext('2d');
      [real, ghost].forEach((c) => c.clearRect(0, 0, this.face.width, this.face.height));
      if (this.imageMode && this.src) this.drawImageItem(real, FACE_K);
      this.drawTextBlock(real, ghost, FACE_K);
      this.checkSafeArea(real);
      this.el.fine.hidden = !(this.tooSmall || this.thinLines);
      [real, ghost].forEach((c) => this.clipToPlate(c, FACE_K));
    }

    // centre of an item in face px, and a context turned to its angle
    enter(ctx, pose, k) {
      ctx.save();
      ctx.translate((this.PW + pose.dx) * k, (this.PH + pose.dy) * k);
      ctx.rotate((pose.a * Math.PI) / 180);
    }

    drawImageItem(ctx, k) {
      const pose = this.items.image;
      const fit = Math.min(IMAGE_BOX[0] / this.src.w, IMAGE_BOX[1] / this.src.h);
      const w = this.src.w * fit * pose.s;
      const h = this.src.h * fit * pose.s;
      this.bounds.image = { w, h };
      this.enter(ctx, pose, k);
      ctx.drawImage(this.imgMask, (-w / 2) * k, (-h / 2) * k, w * k, h * k);
      ctx.restore();
      this.thinLines = this.hasThinLines(w, h);
    }

    // Share of the engraved image thinner than MIN_LINE_MM (both sides of a pixel empty at half that distance).
    hasThinLines(w, h) {
      const k = 12; // px per mm for the test
      const c = this.makeCanvas(w * k, h * k, true);
      if (!c.width || !c.height) return false;
      const ctx = c.getContext('2d');
      ctx.drawImage(this.imgMask, 0, 0, c.width, c.height);
      const a = ctx.getImageData(0, 0, c.width, c.height).data;
      const d = Math.max(1, Math.round((MIN_LINE_MM / 2) * k));
      const on = (x, y) => x >= 0 && y >= 0 && x < c.width && y < c.height && a[(y * c.width + x) * 4 + 3] > 127;
      let ink = 0;
      let thin = 0;
      for (let y = 0; y < c.height; y += 2) {
        for (let x = 0; x < c.width; x += 2) {
          if (!on(x, y)) continue;
          ink += 1;
          if ((!on(x - d, y) && !on(x + d, y)) || (!on(x, y - d) && !on(x, y + d))) thin += 1;
        }
      }
      return ink > 50 && thin / ink > 0.25;
    }

    drawTextBlock(real, ghost, k) {
      const l = LAYOUTS[this.layout()];
      const pose = this.items.text;
      const s = pose.s;
      const font = this.fontName();
      let maxW = 0;
      let top = Infinity;
      let bottom = -Infinity;
      const typedAny = this.hasText();
      this.tooSmall = false;
      [real, ghost].forEach((ctx) => {
        this.enter(ctx, pose, k);
        ctx.fillStyle = '#000';
        ctx.strokeStyle = '#000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
      });
      l.lines.forEach((line) => {
        const input = this.inputs[line.f];
        const typed = input.value.trim();
        // ghosts show where the lines go: on the text product, and on the image product once the customer types
        if (!typed && this.imageMode && !typedAny) return;
        const text = typed || input.placeholder;
        if (!text) return;
        const ctx = typed ? real : ghost;
        let px = line.size * s * k;
        const setFont = () => (ctx.font = `${px}px '${fam(line.small ? SMALL_FONT : font)}', sans-serif`);
        setFont();
        const limit = line.maxW * s * k;
        const measured = ctx.measureText(text).width;
        if (measured > limit) {
          px *= limit / measured;
          setFont();
        }
        ctx.fillText(text, 0, line.y * s * k + (BASELINE[line.small ? SMALL_FONT : font] ?? 0.25) * px);
        const min = line.small ? MIN_FONT_MM : THIN_FONTS.includes(font) ? MIN_FONT_THIN_MM : MIN_FONT_MM;
        if (typed && px / k < min) this.tooSmall = true;
        maxW = Math.max(maxW, Math.min(measured, limit) / k);
        top = Math.min(top, line.y * s - (px / k) * 0.6);
        bottom = Math.max(bottom, line.y * s + (px / k) * 0.6);
      });
      if (l.frame && (typedAny || !this.imageMode)) {
        const ctx = typedAny ? real : ghost;
        const [fw, fh] = l.frame.map((v) => v * s * k);
        ctx.lineWidth = 0.5 * s * k;
        roundRect(ctx, -fw / 2, -fh / 2, fw, fh, 3 * s * k);
        ctx.stroke();
        const g = 1.4 * s * k;
        ctx.lineWidth = 0.25 * s * k;
        roundRect(ctx, -fw / 2 + g, -fh / 2 + g, fw - 2 * g, fh - 2 * g, 2 * s * k);
        ctx.stroke();
        [-fh / 2, fh / 2].forEach((y) => {
          const d = 1.6 * s * k;
          ctx.beginPath();
          ctx.moveTo(0, y - d);
          ctx.lineTo(d, y);
          ctx.lineTo(0, y + d);
          ctx.lineTo(-d, y);
          ctx.closePath();
          ctx.fill();
        });
        maxW = Math.max(maxW, l.frame[0] * s);
        top = Math.min(top, (-l.frame[1] / 2) * s);
        bottom = Math.max(bottom, (l.frame[1] / 2) * s);
      }
      [real, ghost].forEach((ctx) => ctx.restore());
      this.bounds.text = maxW ? { w: maxW, h: bottom - top, cy: (top + bottom) / 2 } : null;
    }

    clipToPlate(ctx, k) {
      const [W, H] = this.geo.plate;
      ctx.save();
      ctx.globalCompositeOperation = 'destination-in';
      roundRect(ctx, 0.6 * k, 0.6 * k, (W - 1.2) * k, (H - 1.2) * k, 4 * k);
      ctx.fill();
      ctx.globalCompositeOperation = 'destination-out';
      this.holes().forEach(([x, y]) => {
        ctx.beginPath();
        ctx.arc((this.PW + x) * k, (this.PH + y) * k, HOLE_R * k, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.restore();
    }

    holes() {
      const [hx, hy] = this.geo.holes;
      return [[-hx, -hy], [hx, -hy], [-hx, hy], [hx, hy]];
    }

    // Anything engraved outside the dashed zone? (the screw heads or the edge would cut it)
    checkSafeArea(ctx) {
      const k = FACE_K;
      const { width, height } = this.face;
      const data = ctx.getImageData(0, 0, width, height).data;
      const e = KEEP_OUT.edge * k;
      const holes = this.holes().map(([x, y]) => [(this.PW + x) * k, (this.PH + y) * k]);
      const r2 = (KEEP_OUT.hole * k) ** 2;
      let bad = false;
      for (let y = 0; y < height && !bad; y += 3) {
        for (let x = 0; x < width; x += 3) {
          if (data[(y * width + x) * 4 + 3] < 60) continue;
          if (x < e || y < e || x > width - e || y > height - e || holes.some(([hx, hy]) => (x - hx) ** 2 + (y - hy) ** 2 < r2)) {
            bad = true;
            break;
          }
        }
      }
      this.el.safe.hidden = !bad;
    }

    // the face mask coloured as the engraving: [shadow, colour]
    inked(mask) {
      const [dark, light, shade] = INK[this.finish];
      const colour = this.makeCanvas(mask.width, mask.height);
      const c = colour.getContext('2d');
      c.drawImage(mask, 0, 0);
      c.globalCompositeOperation = 'source-in';
      const g = c.createLinearGradient(0, 0, mask.width, mask.height);
      g.addColorStop(0, light);
      g.addColorStop(0.5, dark);
      g.addColorStop(1, light);
      c.fillStyle = g;
      c.fillRect(0, 0, mask.width, mask.height);
      let shadow = null;
      if (shade) {
        shadow = this.makeCanvas(mask.width, mask.height);
        const sc = shadow.getContext('2d');
        sc.drawImage(mask, 0, 0);
        sc.globalCompositeOperation = 'source-in';
        sc.fillStyle = `rgba(0, 0, 0, ${shade})`;
        sc.fillRect(0, 0, mask.width, mask.height);
      }
      return { colour, shadow };
    }

    /* ---------- views ---------- */

    // px per mm and the plate centre on the straight view
    flatView() {
      const f = this.geo.flat;
      const vs = (VIEW * 0.94) / f.size[1];
      return { vs, k: f.k * vs, x0: (VIEW - f.size[0] * vs) / 2, y0: (VIEW - f.size[1] * vs) / 2 };
    }

    draw() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.view === 'guitar') this.drawGuitar(this.ctx);
        else this.drawPlate(this.ctx, this.el.guides.checked, true);
      });
    }

    // ghosts = the placeholder lines, only in the editor (never in the preview sent with the order)
    layEngraving(ctx, setTransform, ghosts) {
      const k = FACE_K;
      const layers = [[this.inked(this.face), 1]];
      if (ghosts) layers.unshift([this.inked(this.ghost), 0.35]);
      layers.forEach(([layer, alpha]) => {
        ctx.save();
        setTransform();
        ctx.globalAlpha = alpha;
        if (layer.shadow) ctx.drawImage(layer.shadow, 0.12 * k, 0.12 * k);
        ctx.drawImage(layer.colour, 0, 0);
        ctx.restore();
      });
    }

    drawPlate(ctx, guides, selection) {
      const v = this.flatView();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = '#f2f3f5';
      ctx.fillRect(0, 0, VIEW, VIEW);
      const flat = this.image(this.assets.flat[this.finish]);
      if (flat) ctx.drawImage(flat, v.x0, v.y0, flat.naturalWidth * v.vs, flat.naturalHeight * v.vs);
      const m = this.geo.flat.margin;
      const ox = v.x0 + m * v.k;
      const oy = v.y0 + m * v.k;
      const r = v.k / FACE_K;
      this.layEngraving(ctx, () => ctx.setTransform(r, 0, 0, r, ox, oy), selection);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      if (guides) this.drawGuides(ctx, ox, oy, v.k);
      if (selection) this.drawSelection(ctx, ox, oy, v.k);
    }

    // One dashed outline: the zone we can engrave, inside the edge and around the four screws.
    drawGuides(ctx, ox, oy, k) {
      const e = KEEP_OUT.edge;
      const r = KEEP_OUT.hole;
      const [hx, hy] = this.geo.holes;
      const L = -this.PW + e;
      const R = this.PW - e;
      const T = -this.PH + e;
      const B = this.PH - e;
      const reach = (d) => Math.sqrt(Math.max(0, r * r - d * d)); // where a hole circle meets an edge
      const px = (x) => ox + (this.PW + x) * k;
      const py = (y) => oy + (this.PH + y) * k;
      const notch = (cx, cy, from, to) =>
        ctx.arc(px(cx), py(cy), r * k, Math.atan2(from[1] - cy, from[0] - cx), Math.atan2(to[1] - cy, to[0] - cx), true);
      const tl = [-hx, -hy];
      const tr = [hx, -hy];
      const br = [hx, hy];
      const bl = [-hx, hy];
      ctx.save();
      ctx.setLineDash([7, 5]);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = this.finish === 'black' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(40, 90, 180, 0.5)';
      ctx.beginPath();
      ctx.moveTo(px(tl[0] + reach(T - tl[1])), py(T));
      ctx.lineTo(px(tr[0] - reach(T - tr[1])), py(T));
      notch(tr[0], tr[1], [tr[0] - reach(T - tr[1]), T], [R, tr[1] + reach(R - tr[0])]);
      ctx.lineTo(px(R), py(br[1] - reach(R - br[0])));
      notch(br[0], br[1], [R, br[1] - reach(R - br[0])], [br[0] - reach(B - br[1]), B]);
      ctx.lineTo(px(bl[0] + reach(B - bl[1])), py(B));
      notch(bl[0], bl[1], [bl[0] + reach(B - bl[1]), B], [L, bl[1] - reach(L - bl[0])]);
      ctx.lineTo(px(L), py(tl[1] + reach(L - tl[0])));
      notch(tl[0], tl[1], [L, tl[1] + reach(L - tl[0])], [tl[0] + reach(T - tl[1]), T]);
      ctx.closePath();
      ctx.stroke();
      ctx.restore();
    }

    // the frame of what moves: while dragging and for a moment after a change
    drawSelection(ctx, ox, oy, k) {
      if (!this.gesture && !(this.activeUntil > Date.now())) return;
      const b = this.bounds[this.selected];
      if (!b || (this.selected === 'image' && !this.src)) return;
      const p = this.items[this.selected];
      ctx.save();
      ctx.translate(ox + (this.PW + p.dx) * k, oy + (this.PH + p.dy) * k);
      ctx.rotate((p.a * Math.PI) / 180);
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = 'rgba(220, 60, 30, 0.8)';
      const pad = 1.2 * k;
      ctx.strokeRect((-b.w / 2) * k - pad, ((b.cy || 0) - b.h / 2) * k - pad, b.w * k + 2 * pad, b.h * k + 2 * pad);
      ctx.restore();
    }

    drawGuitar(ctx, ghosts = true) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, VIEW, VIEW);
      const bg = this.image(this.assets.guitar[this.finish]);
      if (bg) ctx.drawImage(bg, 0, 0, VIEW, VIEW);
      const g = this.geo.guitar;
      const [W, H] = this.geo.plate;
      const ux = [(g.tr[0] - g.tl[0]) / W / FACE_K, (g.tr[1] - g.tl[1]) / W / FACE_K];
      const uy = [(g.bl[0] - g.tl[0]) / H / FACE_K, (g.bl[1] - g.tl[1]) / H / FACE_K];
      this.layEngraving(ctx, () => ctx.setTransform(ux[0], ux[1], uy[0], uy[1], g.tl[0], g.tl[1]), ghosts);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }

    /* ---------- pointer: drag, pinch ---------- */

    setupPointer() {
      const c = this.canvas;
      this.pointers = new Map();
      c.addEventListener('pointerdown', (e) => {
        if (this.view !== 'plate') return this.setView('plate');
        c.setPointerCapture(e.pointerId);
        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (this.pointers.size === 1) this.pickAt(e);
        this.gesture = { pose: Object.assign({}, this.items[this.selected]), start: this.pointerState() };
      });
      c.addEventListener('pointermove', (e) => {
        if (!this.pointers.has(e.pointerId) || !this.gesture) return;
        e.preventDefault();
        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const now = this.pointerState();
        const from = this.gesture.pose;
        const k = this.flatView().k * (c.clientWidth / c.width); // css px per mm
        const change = { dx: from.dx + (now.x - this.gesture.start.x) / k, dy: from.dy + (now.y - this.gesture.start.y) / k };
        if (now.d && this.gesture.start.d) change.s = (from.s * now.d) / this.gesture.start.d;
        this.setPose(change);
      });
      const end = (e) => {
        this.pointers.delete(e.pointerId);
        this.gesture = this.pointers.size ? { pose: Object.assign({}, this.items[this.selected]), start: this.pointerState() } : null;
        this.draw();
      };
      c.addEventListener('pointerup', end);
      c.addEventListener('pointercancel', end);
      c.addEventListener('keydown', (e) => {
        const step = { ArrowLeft: [-0.5, 0], ArrowRight: [0.5, 0], ArrowUp: [0, -0.5], ArrowDown: [0, 0.5] }[e.key];
        if (!step) return;
        e.preventDefault();
        const p = this.items[this.selected];
        this.setPose({ dx: p.dx + step[0], dy: p.dy + step[1] });
      });
    }

    pointerState() {
      const pts = Array.from(this.pointers.values());
      const x = pts.reduce((s, p) => s + p.x, 0) / pts.length;
      const y = pts.reduce((s, p) => s + p.y, 0) / pts.length;
      const d = pts.length > 1 ? Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) : 0;
      return { x, y, d };
    }

    // On the image product, a touch on the text or on the image picks what moves.
    pickAt(e) {
      if (!this.imageMode) return;
      const rect = this.canvas.getBoundingClientRect();
      const v = this.flatView();
      const px = ((e.clientX - rect.left) / rect.width) * VIEW;
      const py = ((e.clientY - rect.top) / rect.height) * VIEW;
      const x = (px - VIEW / 2) / v.k;
      const y = (py - VIEW / 2) / v.k;
      const hit = ['text', 'image'].find((kind) => {
        const b = this.bounds[kind];
        if (!b || (kind === 'image' && !this.src)) return false;
        const p = this.items[kind];
        const a = (-p.a * Math.PI) / 180;
        const lx = (x - p.dx) * Math.cos(a) - (y - p.dy) * Math.sin(a);
        const ly = (x - p.dx) * Math.sin(a) + (y - p.dy) * Math.cos(a) - (b.cy || 0);
        return Math.abs(lx) <= b.w / 2 + 2 && Math.abs(ly) <= b.h / 2 + 2;
      });
      if (hit && hit !== this.selected) this.select(hit);
    }

    /* ---------- properties, preview, add to cart ---------- */

    // the current choice, shown on each folded section
    writeSummaries() {
      const set = (key, text) => {
        const el = this.querySelector(`[data-pd-value="${key}"]`);
        if (el) el.textContent = text;
      };
      const lines = LAYOUTS[this.layout()].lines.map((l) => this.inputs[l.f].value.trim()).filter(Boolean);
      set('text', lines.join(' · '));
      const layout = this.querySelector('[data-pd-layout]:checked');
      const name = layout && layout.parentElement.querySelector('[data-pd-layout-name]');
      set('layout', name ? name.textContent : '');
      set('font', this.fontName());
      const p = this.items[this.selected];
      set('position', `${Math.round(p.s * 100)} % · ${p.a}°`);
      const options = this.querySelector('[data-pd-value="options"]');
      if (options) options.textContent = this.el.proof.checked ? options.dataset.label : '';
    }

    describe(pose, kind) {
      const d = this.defaultPose(kind);
      const same = Math.abs(pose.dx - d.dx) < 0.5 && Math.abs(pose.dy - d.dy) < 0.5 && pose.s === d.s && pose.a === 0;
      if (same) return this.dataset.centred || 'As in the layout';
      const parts = [this.dataset.moved || 'Moved'];
      if (pose.s !== d.s) parts.push(`size ${Math.round((pose.s / d.s) * 100)} %`);
      if (pose.a) parts.push(`turned ${pose.a}°`);
      return parts.join(', ');
    }

    writeProperties() {
      const pose = (p) => `dx=${p.dx.toFixed(1)};dy=${p.dy.toFixed(1)};s=${p.s.toFixed(2)};a=${p.a}`;
      const text = this.hasText();
      this.el.textPosition.value = this.describe(this.items.text, 'text');
      this.el.textPose.value = pose(this.items.text);
      this.el.textPosition.disabled = !text;
      this.el.textPose.disabled = !text;
      if (this.imageMode) {
        const has = !!this.src;
        this.el.imagePosition.value = this.describe(this.items.image, 'image');
        this.el.imagePose.value = pose(this.items.image);
        this.el.imagePosition.disabled = !has;
        this.el.imagePose.disabled = !has;
      }
    }

    // The straight view and the guitar view side by side, attached to the order line as _Preview.
    attachPreview() {
      const input = this.querySelector('[data-pd-preview-file]');
      if (!window.DataTransfer) return;
      const out = this.makeCanvas(2 * VIEW, VIEW);
      const ctx = out.getContext('2d');
      const one = this.makeCanvas(VIEW, VIEW);
      this.drawPlate(one.getContext('2d'), false, false);
      ctx.drawImage(one, 0, 0);
      this.drawGuitar(one.getContext('2d'), false);
      ctx.drawImage(one, VIEW, 0);
      out.toBlob(
        (blob) => {
          if (!blob) return;
          try {
            const dt = new DataTransfer();
            dt.items.add(new File([blob], 'plate-preview.jpg', { type: 'image/jpeg' }));
            input.files = dt.files;
          } catch (e) {
            /* old browser: the order still has every text, font and position */
          }
        },
        'image/jpeg',
        0.85
      );
    }

    guard() {
      if (!this.form) return;
      this.form.addEventListener(
        'submit',
        (event) => {
          let problem = null;
          if (this.imageMode) {
            if (!this.src) problem = this.el.file;
            else if (!this.el.rights.checked) problem = this.el.rights;
          } else if (!this.inputs.main.value.trim()) {
            problem = this.inputs.main;
          }
          if (!problem) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          const section = problem.closest('details');
          if (section) section.open = true;
          this.el.missing.hidden = false;
          this.el.missing.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (problem.focus) problem.focus({ preventScroll: true });
        },
        true
      );
    }
  }

  customElements.define('tgf-plate-designer', PlateDesigner);
})();
